import { useCallback } from 'react'

import { ActiveSubscriptionStatuses } from 'models/user/userWorkspaceDTOs'
import { UserIsPlatformUserClient } from 'utils/typeguards'
import { useGetOrganization$, useGetUserClient$ } from 'dataQueries'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'
import type { PlatformUser } from 'models/user'
import { logger } from 'utils/logging'

import { workspaceHasPaidEntitlement } from './workspaceEntitlement'

/**
 * The workspaces an admin may place an order in for a given client: the client
 * is still a member, and the workspace's subscription is in an active state.
 *
 * Shared with `ClientSelectionSection`'s picker so the two cannot drift — the
 * whole point of revalidation is to confirm the stored workspace against the
 * SAME set the picker would have offered.
 */
export const activeOrderableWorkspaces = (userData: PlatformUser | undefined) => {
  if (!userData || !UserIsPlatformUserClient(userData)) return []
  return userData.workspaces.filter((ws) => ActiveSubscriptionStatuses.has(ws.status) && ws.isMember)
}

export type AdminTargetRevalidation =
  /** Not an admin-scoped draft — nothing to check. */
  | 'skip'
  /** The client is still in the stored workspace; the target was refreshed. */
  | 'ok'
  /** The admin has to confirm the client: membership is gone, or unverifiable. */
  | 'needs-review'

/**
 * Re-resolves a resumed admin draft's target client against live data.
 *
 * A draft carries only ids: the client id, the workspace id and (since the
 * resume fix) the email. Everything else the admin gate resolved — the client's
 * name and phone for the meeting-contact prefill, the workspace's paid
 * entitlement, the organization's invoice permission — was lost, and the
 * stored workspace was trusted without asking whether the client is still in it.
 * A draft can sit for up to two weeks; memberships and subscriptions move.
 *
 * So on resume the client is looked up again and the workspace confirmed against
 * `activeOrderableWorkspaces`. When it holds, the target is re-stamped with the
 * fresh contact + entitlement. When it doesn't — or the lookup fails, which is
 * the same thing for our purposes: we cannot claim the workspace is valid — the
 * email is cleared, which is exactly the token `usePfEntryNavigation` and
 * `PropertySelectionDrawer` read as "no client resolved", so the admin lands on
 * the client stage. The workspace id is left in place for the gate to overwrite;
 * the property and cart are untouched.
 */
export function useRevalidateAdminTarget(): () => Promise<AdminTargetRevalidation> {
  const getClient = useGetUserClient$()
  const getOrganization = useGetOrganization$()

  return useCallback(async (): Promise<AdminTargetRevalidation> => {
    const { targetUserEmail, targetWorkspaceId, targetUserId, setAdminTarget } = usePfStore.getState()
    if (!targetUserEmail || !targetWorkspaceId) return 'skip'

    /** Drops the "client resolved" token so the gate re-opens. */
    const sendToGate = () => {
      setAdminTarget(targetUserId ?? '', targetWorkspaceId, { email: null })
      return 'needs-review' as const
    }

    let userData: PlatformUser | undefined
    try {
      userData = (await getClient.mutateAsync({ email: targetUserEmail }))?.data
    }
    catch {
      return sendToGate()
    }

    const workspace = activeOrderableWorkspaces(userData).find((ws) => ws.id === targetWorkspaceId)
    if (!workspace || !userData) return sendToGate()

    // The client's internal id names them as broker of a property the admin creates; without it the create 400s.
    if (!userData.internalId) {
      logger.warn('pf_revamp admin target revalidation: client has no internal id', undefined, { context: { component: 'useAdminTargetRevalidation', action: 'revalidate', metadata: { targetUserId } } })
      return sendToGate()
    }

    // The invoice permission and the PoA requirement are organization-level answers. A client without an
    // organization has neither. A failed read sends the admin back to the gate: an unknown PoA requirement would be
    // read as "owners only", and DocEstate refuses the order when the representatives' PoA is required (QA-602).
    let invoiceAllowed: boolean | null = null
    let requiresPoaRepresentatives = false
    const organizationId = UserIsPlatformUserClient(userData) ? userData.organizationId ?? '' : ''
    if (organizationId) {
      try {
        const organization = (await getOrganization.mutateAsync({ organizationId }))?.data
        invoiceAllowed = organization?.invoiceAllowed ?? null
        requiresPoaRepresentatives = !!organization?.requirePowerOfAttorneyRepresentatives
      }
      catch (error) {
        logger.warn('pf_revamp admin target revalidation: organization read failed', error, { context: { component: 'useAdminTargetRevalidation', action: 'revalidate', metadata: { targetUserId, organizationId } } })
        return sendToGate()
      }
    }
    else {
      logger.info('pf_revamp admin target revalidation: client has no organization', undefined, { context: { component: 'useAdminTargetRevalidation', action: 'revalidate', metadata: { targetUserId } } })
    }

    setAdminTarget(userData.id ?? targetUserId ?? '', targetWorkspaceId, {
      email: targetUserEmail,
      internalId: userData.internalId,
      name: userData.name,
      phone: userData.phone,
      isSubscribed: workspaceHasPaidEntitlement(workspace),
      invoiceAllowed,
      requiresPoaRepresentatives,
    })

    return 'ok'
  }, [getClient, getOrganization])
}
